import { useState } from "react";
import { Play } from "lucide-react";
import {
  disclosureText,
  providerName,
  type ReferenceVideo,
} from "@/features/matches/referenceVideo";
import { ReferenceVideoTheater } from "./ReferenceVideoTheater";

/**
 * "Watch this" — a coach-attached reference video on the player surface
 * (issue #675).
 *
 * **Click-to-load is the privacy mechanism, not a nicety.** Before the viewer
 * presses play this renders a poster served from our own domain and nothing
 * else. The provider `<iframe>` is mounted only in response to the click — now
 * inside `ReferenceVideoTheater` — so a family who never presses play is never
 * disclosed to Google or Vimeo, which is what
 * `docs/legal/beta/beta-privacy-notice.md` and the parental consent form
 * promise them.
 *
 * Two changes that would break that silently, both covered by
 * `tests/e2e/reference-video-privacy.spec.js`:
 *   - pointing the poster at the provider's CDN
 *   - mounting the theater (or an iframe) without an explicit click
 *
 * **This card is a citation, not a player.** It plays nothing inline. The
 * first version did, and there was no height that worked: the card sits inside
 * `FeedbackPlayerModal` (`max-h-[95vh]`, `overflow-y-auto`), so a player
 * competes with the coach's writing for scroll space. Capping its height to
 * protect the words letterboxed the video into a ~10:1 strip; a true 16:9 box
 * pushed the words off screen. Reading and watching want different amounts of
 * room, so watching gets its own screen.
 *
 * The thumbnail keeps 16:9 — it is a picture OF a video — but stays small, so
 * the written feedback above it is still the main thing on the page.
 */
export function ReferenceVideoCard({
  video,
  label = "Watch this",
}: {
  video: ReferenceVideo;
  label?: string;
}) {
  const [open, setOpen] = useState(false);
  const disclosure = disclosureText(video);

  return (
    // Bounded to its own content, not the panel width.
    //
    // The section used to span the full modal (1214px) while its content
    // occupied the left 672px — 544px of empty band with a small thumbnail
    // parked at one end. The reference is SUPPORTING material next to the
    // note's video moment or the observation's board, so it should read as a
    // discrete card, not a full-width row competing with the primary media.
    <section
      data-testid="reference-video-card"
      className="w-fit max-w-full rounded-inset border border-rule bg-elevated/40 p-3"
    >
      <h4 className="text-[0.6rem] font-display uppercase tracking-wider text-ink-muted">
        {label}
      </h4>

      {/* Thumbnail LEFT, text RIGHT on a wide screen; stacked on a phone —
        * it reads the way the coach means it: "watch this, because…".
        *
        * The thumbnail constrains its WIDTH, never its height: `aspect-video`
        * + `max-h` fight each other, the clamp wins, and the letterboxed-strip
        * bug comes back. The text column is capped so a long reason wraps to a
        * readable measure instead of stretching the card. */}
      <div className="mt-1 flex flex-col gap-3 sm:flex-row sm:items-start">
        <button
          type="button"
          onClick={() => setOpen(true)}
          data-testid="reference-video-play"
          aria-label={`Play ${video.title || "reference video"}`}
          aria-haspopup="dialog"
          // 200px on desktop: a still tall enough to read at a glance, short
          // enough that the two or three lines beside it don't leave a gap
          // underneath. Full width on a phone, where it stacks.
          className="group relative block aspect-video w-full shrink-0 overflow-hidden rounded-inset border border-rule bg-canvas focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent sm:w-[200px]"
        >
          {video.thumbUrl ? (
            // Served by US. Pointing this at the provider's CDN is the
            // one-line change that would quietly defeat click-to-load.
            <img
              src={video.thumbUrl}
              alt=""
              data-testid="reference-video-poster"
              className="h-full w-full object-cover"
            />
          ) : (
            // No stored poster — always the case for a direct file, since
            // probing the coach's host to make one is the arbitrary fetch the
            // resolver refuses.
            <span
              aria-hidden
              className="flex h-full w-full items-center justify-center text-[0.6rem] font-display uppercase tracking-wider text-ink-muted"
            >
              {providerName(video.provider)}
            </span>
          )}
          <span
            aria-hidden
            className="absolute inset-0 flex items-center justify-center bg-black/10 transition-colors group-hover:bg-black/20"
          >
            {/* The whole still is the tap target, so the circle is only the
              * visual affordance — 48px keeps it proportionate on a 240px
              * thumbnail while the button itself stays far above Apple's
              * 44pt minimum. */}
            <span className="flex h-12 w-12 items-center justify-center rounded-pill border border-rule bg-surface shadow-sm">
              <Play className="h-5 w-5 translate-x-[1px] text-ink" />
            </span>
          </span>
        </button>

        {/* The reason, beside the still on desktop and beneath it on a phone. */}
        {/* Capped at 22rem so a long "why this video" wraps to a readable
          * measure rather than stretching the card across the panel. */}
        <div className="min-w-0 flex-1 sm:max-w-[22rem]">
          {video.title ? (
            <p className="text-sm font-semibold text-ink">{video.title}</p>
          ) : null}
          {video.label ? (
            <p className="mt-0.5 text-xs text-ink-muted">{video.label}</p>
          ) : null}
          {disclosure ? (
            <p
              data-testid="reference-video-disclosure"
              className="mt-1.5 text-xs text-ink-muted"
            >
              {disclosure}
            </p>
          ) : null}
        </div>
      </div>

      {open ? (
        <ReferenceVideoTheater
          video={video}
          label={label}
          onClose={() => setOpen(false)}
        />
      ) : null}
    </section>
  );
}
